import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import useContact from "../hooks/useContact";
import useReport from "../hooks/useReport";
import { Bin } from "../models/Bin";
import { DisbursementAccountFundingType } from "../models/DisbursementAccountFundingType";
import { Ica } from "../models/Ica";
import { Program } from "../models/Program";
import { ContactApi } from "../services/api/ContactApi";
import { Country } from "../models/Country";
import { AdjustmentType } from "../models/AdjustmentType";
import { ReferenceApi } from "../services/api/ReferenceApi";
import { EmlReasonCode } from "../models/EmlReasonCode";
import { Portfolio } from "../models/Portfolio";
import { AuthRequestDeclineReason } from "../models/AuthRequestDeclineReason";

import { Developer } from "../models/Developer";

interface SearchCriteriaRedReportsProps {
  handleViewReport: () => void;
  handleDownloadReport: () => void;
  useIcaValue?: boolean;
  useIcas?: boolean;
  useBins?: boolean;
  useBin?: boolean;
  usePrograms?: boolean;
  useToggles?: boolean;
  useBankIds?: boolean;
  useCountries?: boolean;
  showToDate?: boolean;
  useDisbursementAccountFundingTypes?: boolean;
  useAdjustmentTypes?: boolean;
  useEmlReasonCodes?: boolean;
  usePortfolios?: boolean;
  useFirstLastName?: boolean;
  useAuthDeclineReason?: boolean;
  useDevelopers?: boolean;
}

const SearchCriteriaRedReports = ({
  handleViewReport,
  handleDownloadReport,
  useIcaValue,
  useIcas = false,
  useBins = false,
  usePrograms = false,
  useToggles = false,
  useBankIds = false,
  useCountries = false,
  showToDate = true,
  useBin = false,
  useDisbursementAccountFundingTypes = false,
  useAdjustmentTypes = false,
  useEmlReasonCodes = false,
  usePortfolios = false,
  useFirstLastName = false,
  useAuthDeclineReason = false,
  useDevelopers = false,
}: SearchCriteriaRedReportsProps) => {
    const { icas, bins, setBins, programs, bankIds, setBankIds, countries, setCountries, portfolios, setPortfolios, developers, setDevelopers } = useContact();
  const { reportInputs, setReportInputs } = useReport();
  const [disbursementAccountFundingTypes, setDisbursementAccountFundingTypes] = useState<
    DisbursementAccountFundingType[]
  >([]);
  const [adjustmentTypes, setAdjustmentTypes] = useState<
        AdjustmentType[]
  >([]);
  const [emlReasonCodes, setEmlReasonCodes] = useState<
        EmlReasonCode[]
  >([]);
  const [authDeclineReasons, setAuthDeclineReasons] = useState<
      AuthRequestDeclineReason[]
  >([]);

  useEffect(() => {
      if (useDevelopers && (!developers || (developers?.length ?? 0) < 1)) {
          ContactApi.getDevelopers().then((res) => {
              const developers = res.data;

              developers.sort((a, b) =>
                  a.name.localeCompare(b.name, "en", {
                      sensitivity: "base"
                  })
              );

              setDevelopers(developers);
          });
      }
  }, [developers]);

  useEffect(() => {
    if (useCountries && (!countries || (countries?.length ?? 0) < 1)) {
      ContactApi.getCountries().then((res) => {
        const countries = res.data;

        countries.sort((a, b) =>
          a.countryValue.localeCompare(b.countryValue, "en", { 
            sensitivity: "base" })
        );

        setCountries(countries);
      });
    }
  }, [countries]);


  useEffect(() => {
    if ((useBins || useBin) && (!bins || (bins?.length ?? 0) < 1)) {
      ContactApi.getBins().then((res) => {
        const bins = res.data;
        bins.sort((a, b) =>
          a.binValue.localeCompare(b.binValue, "en", {
            sensitivity: "base",
          })
        );
        setBins(bins);
      });
    }
  }, [bins]);

  useEffect(() => { 
    if (useBankIds  && !bankIds)   
      ContactApi.getBankIds().then((res) => {
        const ids = res.data;                               
        if ((!ids || (ids?.length ?? 0) < 1)){
           setBankIds("");
        } else {
          setBankIds(ids.join(","));
        }
      });    
  }, []);

  useEffect(() => {
    if (
      useDisbursementAccountFundingTypes &&
      (!disbursementAccountFundingTypes ||
        (disbursementAccountFundingTypes?.length ?? 0) < 1)
    ) {
      ReferenceApi.getDisbursementAccountFundingTypes().then((res) => {
        const types = res.data;
        types.sort((a, b) =>
          a.name.localeCompare(b.name, "en", {
            sensitivity: "base",
          })
        );
        setDisbursementAccountFundingTypes(types);
      });
    }
  }, [useDisbursementAccountFundingTypes, disbursementAccountFundingTypes]);

  useEffect(() => {
      if (
          useAdjustmentTypes &&
          (!adjustmentTypes ||
              (adjustmentTypes?.length ?? 0) < 1)
      ) {
          ReferenceApi.getAdjustmentTypes().then((res) => {
              const types = res.data;
              types.sort((a, b) =>
                  a.description.localeCompare(b.description, "en", {
                      sensitivity: "base",
                  })
              );
              setAdjustmentTypes(types);
          });
      }
  }, [useAdjustmentTypes, adjustmentTypes]);

  useEffect(() => {
      if (
          useEmlReasonCodes &&
          (!emlReasonCodes ||
              (emlReasonCodes?.length ?? 0) < 1)
      ) {
          ReferenceApi.getEmlReasonCodes().then((res) => {
              const types = res.data;
              types.sort((a, b) =>
                  a.description.localeCompare(b.description, "en", {
                      sensitivity: "base",
                  })
              );
              setEmlReasonCodes(types);
          });
      }
  }, [useEmlReasonCodes, emlReasonCodes]);

  useEffect(() => {
      if (usePortfolios && (!portfolios || (portfolios?.length ?? 0) < 1)) {
          ContactApi.getPortfolios().then((res) => {
              const portfolios = res.data;

              portfolios.sort((a, b) =>
                  a.description.localeCompare(b.description, "en", {
                      sensitivity: "base"
                  })
              );

              setPortfolios(portfolios);
          });
      }
  }, [portfolios]);

  useEffect(() => {
      if (
          useAuthDeclineReason &&
          (!authDeclineReasons ||
              (authDeclineReasons?.length ?? 0) < 1)
      ) {
          ReferenceApi.getAuthRequestDeclineReasons().then((res) => {
              const types = res.data;
              types.sort((a, b) =>
                  a.description.localeCompare(b.description, "en", {
                      sensitivity: "base",
                  })
              );
              setAuthDeclineReasons(types);
          });
      }
  }, [authDeclineReasons, setAuthDeclineReasons]);

  const handleDateChange = (id: string, date: Date | null) => {
    setReportInputs({
      ...reportInputs,
      [id]: date,
    });
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.currentTarget;
    setReportInputs({
      ...reportInputs,
      [id]: value,
    });
  };

  const handleMultipleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValues: string[] = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );
    const { id } = e.currentTarget;
    setReportInputs({
      ...reportInputs,
      [id]: selectedValues,
    });
  };

  const handleCheckboxChange = (e:React.ChangeEvent<HTMLInputElement>) => {
    const { id } = e.currentTarget;
    const isChecked = (e.currentTarget as HTMLInputElement).checked;
    setReportInputs({
      ...reportInputs,
      reportToggles:{
        ...reportInputs.reportToggles,
        groupByIca: id === "groupByIca" ? isChecked : reportInputs.reportToggles?.groupByIca,
        groupByBin: id === "groupByBin" ? isChecked : reportInputs.reportToggles?.groupByBin,
        groupByProgram: id === "groupByProgram" ? isChecked : reportInputs.reportToggles?.groupByProgram,
        groupByDate: id === "groupByDate" ? isChecked : reportInputs.reportToggles?.groupByDate,
        showSubtotals: id === "showSubtotals" ? isChecked : reportInputs.reportToggles?.showSubtotals,
      }
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = e.currentTarget;
      setReportInputs({
          ...reportInputs,
          [id]: value,
      });
  };

  const hasRequiredData = () => {
    let hasRequiredData = reportInputs?.fromDate !== null && reportInputs?.toDate !== null;
   
    if (useIcaValue === true)
        hasRequiredData = hasRequiredData && (reportInputs?.icaValue?.length ?? 0) > 0;

    if (useDevelopers === true)
        hasRequiredData = hasRequiredData && (reportInputs?.developerId?.length ?? 0) > 0;

    return hasRequiredData;
  };

  const isValid = hasRequiredData();

  return (
    <div className="report-inputs">
      <div className="row d-flex justify-content-center">
        <div className="col-4">
          <DatePicker
            className="form-control form-control-sm"
            placeholderText=" From Date"
            showIcon
            id="fromDate"
            toggleCalendarOnIconClick
            selected={reportInputs?.fromDate ?? ""}
            onChange={(date: Date | null) => handleDateChange("fromDate", date)}
            maxDate={reportInputs?.toDate ?? new Date()}
            showMonthDropdown
            showYearDropdown
            yearDropdownItemNumber={15}
            onMonthChange={(date: Date | null) =>
              handleDateChange("fromDate", date)
            }
            onYearChange={(date: Date | null) =>
              handleDateChange("fromDate", date)
            }
          />
        </div>
        {showToDate !== false && (
          <div className="col-4">
            <DatePicker
              className="form-control form-control-sm"
              placeholderText=" To Date"
              showIcon
              id="toDate"
              toggleCalendarOnIconClick
              selected={reportInputs?.toDate ?? ""}
              onChange={(date: Date | null) => handleDateChange("toDate", date)}
              minDate={reportInputs?.fromDate ?? ""}
              showMonthDropdown
              showYearDropdown
              yearDropdownItemNumber={15}
              onMonthChange={(date: Date | null) =>
                handleDateChange("toDate", date)
              }
              onYearChange={(date: Date | null) =>
                handleDateChange("toDate", date)
              }
            />
          </div>
        )}
      </div>
      <div className="row mt-3 mb-4">
        {useIcaValue === true &&
        <div className="col-6">
        <label htmlFor="icaValue" className="form-label">
          ICA:
        </label>
        <select
          className="dropdown form-control form-control-sm"
          value={reportInputs?.icaValue || ""}
          id="icaValue"
          name="icaValue"
          onChange={handleSelectChange}
        >
          <option value={""}>--Select--</option>
          {icas?.map((i: Ica) => (
            <option key={i.icaValue} value={i.icaValue}>
              {i.icaValue} - {i.description}
            </option>
          ))}
        </select>
      </div>}
        {useIcas && <div className="col-6">
          <label htmlFor="icaList" className="form-label">
            ICA:
          </label>
          <select
            multiple={true}
            className="dropdown form-control form-control-sm"
            value={
              (reportInputs?.icaList?.length ?? 0) > 0
                ? reportInputs?.icaList
                : [""]
            }
            id="icaList"
            name="icaList"
            onChange={handleMultipleSelectChange}
          >
            {icas?.map((i: Ica) => (
              <option key={i.icaValue} value={i.icaValue}>
                {i.icaValue} - {i.description}
              </option>
            ))}
          </select>
        </div>}
        {(useBins === true || useBin === true) && (
          <div className="col-6">
            <label htmlFor="binList" className="form-label">
              BIN:
            </label>
            <select
              multiple={useBins}
              className="dropdown form-control form-control-sm"
              value={
                (reportInputs?.binList?.length ?? 0) > 0
                  ? reportInputs?.binList
                  : [""]
              }
              id="binList"
              name="binList"
              onChange={handleMultipleSelectChange}
            >
              {useBin && <option value={""}>All BINs</option>}
              {bins?.map((b: Bin) => (
                <option key={b.binValue} value={b.binValue}>
                  {b.binValue} - {b.description}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="row mt-3 mb-4">

        {useDevelopers === true && (
          <div className="col-6">
            <label htmlFor="developerId" className="form-label">
                Developer:
            </label>
            <select
              multiple={false}
              className="dropdown form-control form-control-sm"
              value={reportInputs?.developerId || ""}
              id="developerId"
              name="developerId"
              onChange={handleSelectChange}
               >
              <option value={""}>--Select--</option>
              {developers?.map((p: Developer) => (
                  <option key={p.id} value={p.id}>
                      {p.name}
                  </option>
              ))}
            </select>
          </div>
        )}

        {usePrograms === true && (
          <div className="col-6">
            <label htmlFor="programIds" className="form-label">
              Program:
            </label>
            <select
              multiple={true}
              className="dropdown form-control form-control-sm"
              value={
                (reportInputs?.programIds?.length ?? 0) > 0
                  ? reportInputs?.programIds
                  : [""]
              }
              id="programIds"
              name="programIds"
              onChange={handleMultipleSelectChange}
            >
              {programs?.map((p: Program) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {useCountries === true && (
          <div className="col-6">
            <label htmlFor="countryList" className="form-label">
              Country:
            </label>
            <select
              multiple={true}
              className="dropdown form-control form-control-sm"
              value={
                (reportInputs?.countryList?.length ?? 0) > 0
                  ? reportInputs?.countryList
                  : [""]
              }
              id="countryList"
              name="countryList"
              onChange={handleMultipleSelectChange}
            >
              {countries?.map((b: Country) => (
                <option key={b.countryValue} value={b.countryValue}>
                   {b.countryValue}-{b.description}
                </option>
              ))}
            </select>
          </div>
        )}

        {useToggles === true && (
          <div className="col-5 bordered mt-2 ms-3 pt-1 pb-1">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="groupByIca"
                checked={
                  (reportInputs?.reportToggles?.groupByIca ?? "") === true
                }
                onChange={handleCheckboxChange}
              />
              <label className="form-check-label" htmlFor="groupByIca">
                Group By ICA
              </label>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="groupByBin"
                checked={
                  (reportInputs?.reportToggles?.groupByBin ?? "") === true
                }
                onChange={handleCheckboxChange}
              />
              <label className="form-check-label" htmlFor="groupByBin">
                Group By Bin
              </label>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="groupByProgram"
                checked={
                  (reportInputs?.reportToggles?.groupByProgram ?? "") === true
                }
                onChange={handleCheckboxChange}
              />
              <label className="form-check-label" htmlFor="groupByProgram">
                Group By Program
              </label>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="groupByDate"
                checked={
                  (reportInputs?.reportToggles?.groupByDate ?? "") === true
                }
                onChange={handleCheckboxChange}
              />
              <label className="form-check-label" htmlFor="groupByDate">
                Group By Date
              </label>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="showSubtotals"
                checked={
                  (reportInputs?.reportToggles?.showSubtotals ?? "") === true
                }
                onChange={handleCheckboxChange}
              />
              <label className="form-check-label" htmlFor="showSubtotals">
                Show Subtotals
              </label>
            </div>
          </div>
        )}

        {useDisbursementAccountFundingTypes === true && (
          <div className="col-6">
            <label htmlFor="fundingTypeId" className="form-label">
              Funding Type:
            </label>
            <select
              className="dropdown form-control form-control-sm"
              value={reportInputs?.fundingTypeId || ""}
              id="fundingTypeId"
              name="fundingTypeId"
              onChange={handleSelectChange}
            >
              <option value={""}>--Select--</option>
              {disbursementAccountFundingTypes?.map((type: DisbursementAccountFundingType) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
              )}

              {usePortfolios === true && (
                  <div className="col-6">
                      <label htmlFor="portfolioId" className="form-label">
                          Portfolio:
                      </label>
                      <select
                          className="dropdown form-control form-control-sm"
                          value={reportInputs?.portfolioId || ""}
                          id="portfolioId"
                          name="portfolioId"
                          onChange={handleSelectChange}
                      >
                          <option value={""}>--Select--</option>
                          {portfolios?.map((p: Portfolio) => (
                              <option key={p.id} value={p.id}>
                                  {p.description}
                              </option>
                          ))}
                      </select>
                  </div>
              )}

     </div>

      <div className="row mt-3 mb-4">
      {useAdjustmentTypes === true && (
          <div className="col-6">
              <label htmlFor="adjustmentType" className="form-label">
                  Adjustment Type:
              </label>
              <select
                  className="dropdown form-control form-control-sm"
                  value={reportInputs?.adjustmentType || ""}
                  id="adjustmentType"
                  name="adjustmentType"
                  onChange={handleSelectChange}
              >
                  <option value={""}>--Select--</option>
                  {adjustmentTypes?.map((type: AdjustmentType) => (
                      <option key={type.id} value={type.description}>
                          {type.description}
                      </option>
                  ))}
              </select>
          </div>
      )}
     
      {useEmlReasonCodes === true && (
          <div className="col-6">
              <label htmlFor="emlReasonCodeId" className="form-label">
                  EML Reason Code:
              </label>
              <select
                  className="dropdown form-control form-control-sm"
                  value={reportInputs?.emlReasonCodeId || ""}
                  id="emlReasonCodeId"
                  name="emlReasonCodeId"
                  onChange={handleSelectChange}
              >
                  <option value={""}>--Select--</option>
                  {emlReasonCodes?.map((type: EmlReasonCode) => (
                      <option key={type.id} value={type.id}>
                          {type.description}
                      </option>
                  ))}
              </select>
          </div>
      )}
     
      {useFirstLastName === true && (
          <div className="col-6">
              <label htmlFor="lastName" className="form-label">
                  User's Last Name:
              </label>
              <input
                  id="lastName"
                  name="lastName"
                  className="form-control form-control-sm"
                  type="text"
                  onChange={handleInputChange}
              />
              <label htmlFor="firstName" className="form-label">
                  User's First Name:
              </label>
              <input
                  id="firstName"
                  name="firstName"
                  className="form-control form-control-sm"
                  type="text"
                  onChange={handleInputChange}
              />
          </div>
      )}

      {useAuthDeclineReason === true && (
          <div className="col-6">
              <label htmlFor="authDeclineReason" className="form-label">
                  Decline Reason:
              </label>
              <select
                  className="dropdown form-control form-control-sm"
                  value={reportInputs?.authRequestDeclineReason || ""}
                  id="authRequestDeclineReason"
                  name="authRequestDeclineReason"
                  onChange={handleSelectChange}
              >
                  <option value={""}>--Select--</option>
                  {authDeclineReasons?.map((type: AuthRequestDeclineReason) => (
                      <option key={type.id} value={type.description}>
                          {type.description}
                      </option>
                  ))}
              </select>
          </div>
      )}
    </div>

      <div className="row mt-3 mb-2">
        <div className="col-6">
          <div className="d-grid text-center">
            <input
              type="submit"
              className="btn btn-sm btn-secondary"
              value="View Report"
              disabled={!isValid}
              onClick={handleViewReport}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="d-grid text-center">
            <input
              type="submit"
              className="btn btn-sm btn-secondary"
              value="Download Report"
              disabled={!isValid}
              onClick={handleDownloadReport}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchCriteriaRedReports;
